/**
 *
 * Redux store configuration
 *
 */
import { combineReducers, configureStore } from '@reduxjs/toolkit'
import { produce } from 'immer'
import { bandOrder } from '~/lib/livePins'
import { fillVocabPins, SHADER_PIN, vocabPinValues } from '~/lib/params'
import { fillSourcesOf } from '~/lib/sourceFill'
import { bindSourceWrites } from '~/lib/sourceWrites'
import { persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { getPersistKey } from '~/store/persistUserId'

// Reducers
import api from '~/store/api/base'
import appReducer from '~/store/app'
import authReducer from '~/store/auth'
import cameraReducer from '~/store/camera'
import editorReducer from '~/store/editor'
import inputReducer from '~/store/input'
import reconcileInput from '~/store/input/reconcile'
import keyBindingsReducer from '~/store/keyBindings'
import reconcileKeyBindings from '~/store/keyBindings/reconcile'
import liveReducer from '~/store/live'
import locationReducer from '~/store/location'
import mapsReducer from '~/store/maps'
import outputMappingReducer from '~/store/outputMapping'
import playbackReducer from '~/store/playback'
import pointEditorReducer from '~/store/pointEditor'
import selectionReducer from '~/store/selection'
import settingsReducer from '~/store/settings'
import reconcileSettings from '~/store/settings/reconcile'
import tutorialReducer from '~/store/tutorial'
import uiReducer from '~/store/ui'
import reconcileUI from '~/store/ui/reconcile'
import videoFileReducer from '~/store/videoFile'

// Middleware
import apiSync from '~/store/middleware/apiSync'
import confirmMiddleware from '~/store/middleware/confirm'
import historyMiddleware, { historyReducer } from '~/store/middleware/history'
import locationSync, { initLocationSync } from '~/store/middleware/locationSync'
import mapShapes from '~/store/middleware/mapShapes'
import parameterSync, { observeChannelFlush } from '~/store/middleware/liveSync'
import paramChannel from '~/lib/paramChannel'
import paramChannelSync from '~/store/middleware/paramChannelSync'
import playbackSync from '~/store/middleware/playbackSync'
import pinsSync from '~/store/middleware/pinsSync'
import promptHistory from '~/store/middleware/promptHistory'
import syncObjects from '~/store/middleware/syncObjects'
import { IS_DEV, IS_OUTPUT } from '~/lib/constants'

const REORDERS = new Set(['live/sortObjects', 'live/bringObjectToFront', 'live/sendObjectToBack'])

// Pinned root objects stay in their bands, whatever moved them; only a reorder of the pinned object itself changes its band.
const keepBands = (prev, next, action) => {
	const order = next.live?.objects?.root?.children
	const pins = next.playback?.pins?.objects
	if (!order || !pins) return next
	const prevOrder = prev?.live?.objects?.root?.children
	if (order === prevOrder && pins === prev?.playback?.pins?.objects) return next
	const pinned = new Set(order.filter(id => id in pins))
	if (!pinned.size) return next
	const moved = REORDERS.has(action.type) ? new Set([action.payload?.draggedIds ?? action.payload].flat()) : undefined
	const children = bandOrder(order, pinned, prevOrder, moved)
	if (children === order) return next
	const { live } = next
	return { ...next, live: { ...live, objects: { ...live.objects, root: { ...live.objects.root, children } } } }
}

const sameSet = (a, b) => a.size === b.size && [...a].every(x => b.has(x))

// Every shader a fill shows holds the vocabulary pins: refilled on a load or a whole-bag write, when the shown shaders change, and when a pinned value moves.
const keepVocabPins = (prev, next, action) => {
	const layer = next.playback?.pins?.source_settings?.[SHADER_PIN]
	if (!layer || !next.live?.objects) return next
	// A preset load, or a whole bag written at once (a shader preset's Load, Reset): a pin outranks both.
	const load =
		(action.type === 'live/setLiveObject' && action.meta?.pins !== undefined) ||
		action.type === 'live/setSourceValues'
	const moved = layer !== prev?.playback?.pins?.source_settings?.[SHADER_PIN]
	const shown =
		next.live.objects !== prev?.live?.objects &&
		!sameSet(fillSourcesOf(next.live.objects), fillSourcesOf(prev?.live?.objects))
	if (!load && !moved && !shown) return next
	const held = vocabPinValues(next.playback.pins)
	if (!held) return next
	const live = produce(next.live, d => fillVocabPins(d, held))
	return live === next.live ? next : { ...next, live }
}

const reducers = combineReducers({
	api: api.reducer,
	app: appReducer,
	auth: authReducer,
	camera: IS_OUTPUT
		? cameraReducer
		: persistReducer(
				{
					key: getPersistKey('camera'),
					storage,
					whitelist: ['device', 'camera_settings'],
				},
				cameraReducer
			),
	editor: editorReducer,
	history: historyReducer,
	key_bindings: persistReducer(
		{
			key: getPersistKey('key_bindings'),
			storage,
			whitelist: ['bindings'],
			stateReconciler: reconcileKeyBindings,
		},
		keyBindingsReducer
	),
	// Controller bindings travel with the operator, not the project — per user, like `key_bindings`. The MIDI runtime state is not persisted.
	input: persistReducer(
		{
			key: getPersistKey('input'),
			storage,
			whitelist: ['bindings', 'midiOn', 'disabledInputs'],
			stateReconciler: reconcileInput,
		},
		inputReducer
	),
	live: liveReducer,
	location: locationReducer,
	maps: mapsReducer,
	outputMapping: persistReducer(
		{
			key: getPersistKey('outputMapping'),
			storage,
			whitelist: ['outputs', 'preview', 'on_output'],
		},
		outputMappingReducer
	),
	playback: persistReducer(
		{
			key: getPersistKey('playback'),
			storage,
			whitelist: ['hold_duration', 'fade_duration', 'current_preset_index'],
		},
		playbackReducer
	),
	point_editor: pointEditorReducer,
	selection: selectionReducer,
	tutorial: persistReducer(
		{
			key: getPersistKey('tutorial'),
			storage,
			whitelist: ['isFirstTime'],
		},
		tutorialReducer
	),
	ui: persistReducer(
		{
			key: getPersistKey('ui'),
			storage,
			whitelist: ['show', 'panels', 'dock'],
			stateReconciler: reconcileUI,
		},
		uiReducer
	),
	videoFile: videoFileReducer,
	settings: persistReducer(
		{
			key: getPersistKey('settings'),
			storage,
			// A diagnostic tap, not a preference: a persisted debug view would greet the next reload with no visible cause.
			blacklist: ['output_layer'],
			/* Persisted settings outlive the schema that wrote them, and the default reconciler hard-sets every stored key over the initial state — so a value the schema no longer permits survives every reload. This drops those; see ~/store/settings/reconcile. */
			stateReconciler: reconcileSettings,
		},
		settingsReducer
	),
})

const store = configureStore({
	reducer: (s, action) => keepVocabPins(s, keepBands(s, reducers(s, action), action), action),
	middleware: d =>
		d({
			// `api` is RTK Query's cache — the largest slice by far (~176KB) and one nothing hand-mutates. Both dev checks deep-walk every slice on EVERY dispatch, so leaving it in doubles the cost of a slider move in development for no signal. Stripped entirely in production; this only affects dev.
			immutableCheck: { ignoredPaths: ['api'] },
			serializableCheck: {
				ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
				ignoredPaths: ['api'],
			},
		}).concat(
			confirmMiddleware,
			api.middleware,
			historyMiddleware,
			locationSync.middleware,
			parameterSync.middleware,
			paramChannelSync.middleware,
			promptHistory.middleware,
			playbackSync.middleware,
			pinsSync.middleware,
			apiSync.middleware,
			syncObjects.middleware,
			mapShapes
		),
})

// Initialize location sync
initLocationSync(store)

// The channel's UI clock, started with the store so it lives exactly as long as the channel it drains. A worker interval, so a backgrounded tab keeps delivering.
paramChannel.start()

// The wire's clock for every channel-only write — a fade and a gesture alike. Registered here rather than at import, so the sender reads THIS store rather than a module-scope singleton.
observeChannelFlush(store.getState)
bindSourceWrites(store)

if (IS_DEV) {
	window.store = store
	// Beside the store, because a param's current value lives in the CHANNEL, not in `live` — driving or reading one from devtools or a capture rig through Redux tests the mirror instead.
	window.paramChannel = paramChannel
	// Shader-source console driver — see lib/shaderProbe.js.
	import('~/lib/shaderProbe').then(({ probe }) => {
		window.probe = probe
	})
}

export default store

export const persistor = persistStore(store)

// The local-storage write inside a flush is synchronous, so the last write lands before the page goes.
if (typeof window !== 'undefined') window.addEventListener('pagehide', () => persistor.flush())
